import * as React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { createRoot, Root } from 'react-dom/client';
import { useContext, useEffect } from 'react';
import { MentionEmployee } from '../types';
import { InnerModalContext } from '../../../../../context/InnerModalContext';
import { CommentContext } from '../Context/CommentContext';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { Maybe } from '../../../../../utils/maybe';

const i18Path = 'comments';

const useEmployeeMentionSpansHook = (
  ref: React.RefObject<HTMLDivElement>,
  id: Maybe<string>,
  mentionedEmployees: MentionEmployee[],
  text: string,
) => {
  const { setIsInnerModalOpen } = useContext(InnerModalContext);
  const { setSelectedEmployee } = useContext(CommentContext);
  const t = useNamespacedTranslation(i18Path);

  // This is used to add onClick to mentioned employees span
  // And a tooltip for those who doesn't have access
  useEffect(() => {
    const spans = ref?.current?.querySelectorAll(
      `.mention[data-comment-id="${id}"]`,
    );

    const roots: Root[] = [];

    spans?.forEach((span) => {
      const employeeId = span.getAttribute('data-employee-id');
      const employee = mentionedEmployees.find((e) => e.id === employeeId);

      if (!employeeId || !employee) return;

      const handleClick = () => {
        setSelectedEmployee(employee);
        setIsInnerModalOpen(true);
      };

      let spanItem = <span onClick={handleClick}>@{employee.name}</span>;

      if (!employee.hasAccess) {
        const tooltip = (
          <Tooltip id={`tooltip-${id}-${employeeId}`}>
            {t('cantAccess')}
          </Tooltip>
        );

        spanItem = (
          <OverlayTrigger key={employeeId} overlay={tooltip}>
            {spanItem}
          </OverlayTrigger>
        );
      }

      // Render the interactive mention into a fresh child container of the
      // source span. The container and its content carry no `.mention` /
      // `data-comment-id` attributes, so only the source span matches the
      // enhancement selector: the effect re-run and StrictMode's double-invoke
      // stay idempotent and never enhance an already-rendered child. Styling
      // stays on the source span's own classes.
      const container = document.createElement('span');
      // createRoot commits asynchronously, so seed the container with the
      // mention's current text before swapping it in. This keeps the source
      // span showing the name from the moment it loses its server text until
      // React's first commit replaces the text with the interactive span,
      // avoiding an empty flicker. replaceChildren detaches the previous run's
      // container; its root is unmounted (deferred) in cleanup.
      container.textContent = span.textContent;
      span.replaceChildren(container);
      const root = createRoot(container);
      root.render(spanItem);
      roots.push(root);
    });

    return () => {
      // Defer unmount to avoid React's "synchronous unmount during render"
      // warning; the containers are already detached so this never races a
      // new createRoot on the same node.
      roots.forEach((root) => queueMicrotask(() => root.unmount()));
    };
  }, [
    id,
    mentionedEmployees,
    ref,
    setIsInnerModalOpen,
    setSelectedEmployee,
    t,
    text,
  ]);
};

export default useEmployeeMentionSpansHook;
